import { Action } from "~/modules/actions/Action";
import { CbmImpl } from "~/modules/cbm/CbmImpl";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";
import { getLogger } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { findSupervisorOnCall } from "~/modules/actions/ActionUtils";




export interface CoachCallOptions {
    



    targetWorkerSid?: string;
}































export class CoachCall implements Action<Promise<void>> {
    readonly #taskSid: string;

    readonly #options?: CoachCallOptions;

    




    constructor(taskSid: string, options?: CoachCallOptions) {
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: {}): Promise<void> {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();

        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const logger = getLogger(context)("CoachCall");
        const worker = taskRouter.worker;

        const checkUtils = {
            actionName: "CoachCall",
            moduleName: "actions/Voice",
            context,
            analytics,
            logger,
            worker,
            event: EVENTS.CoachCallCompleted,
            extra: { taskSid: this.#taskSid }
        };

        try {
            const task = await taskRouter.workspace.fetchTask(this.#taskSid);
            const participants = await cbm.getParticipantsByTask(task);

            const supervisor = findSupervisorOnCall(participants, this.#taskSid, checkUtils);

            const targetWorkerSid = this.#options?.targetWorkerSid;
            const agents = participants.filter((p) => String(p.type) === "agent");
            const target = targetWorkerSid
                ? agents.find((p) => p.routingProperties?.workerSid === targetWorkerSid)
                : agents[0];
            const targetCallSid = target?.mediaProperties?.callSid as string | undefined;

            if (!target || !targetCallSid) {
                const errorMsg = `CoachCall: Cannot resolve target agent call SID for task ${this.#taskSid}`;
                logger.error(errorMsg);
                sendWorkerTrackingEvent(EVENTS.CoachCallCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    targetWorkerSid
                });
                throw createFlexSdkError(context)(
                    ErrorCode.FailedToCoachCall,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                );
            }

            await cbm.modifyVoiceParticipant(task, supervisor.participantSid, {
                muted: false,
                coaching: true,
                callSidToCoach: targetCallSid
            });

            sendWorkerTrackingEvent(EVENTS.CoachCallCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                targetWorkerSid: target.routingProperties?.workerSid,
                targetCallSid,
                supervisorParticipantSid: supervisor.participantSid
            });
        } catch (error: unknown) {
            const errorMsg = `CoachCall: Failed to coach call for task ${this.#taskSid}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.CoachCallCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid
            });
            throw createFlexSdkError(context)(
                ErrorCode.FailedToCoachCall,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error as Error
            );
        }
    }
}
